import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  MenuItem,
  TextField,
  Divider,
  Box,
  Tab,
  Tabs,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { renderComponentEnum, timePeriodEnum } from '../../../../pages/SalesPortalPage/LayoutV1/data/type';
import dayjs, { Dayjs } from 'dayjs';

type TimeperiodSelectProps = {
  timeperiod: Array<{ code: string; name: string }>;
  selectedTimeperiod: string;
  handleClickChangeTimeperiod: (code: string, label?: string, startDate?: string, endDate?: string) => void;
  onLastOptionSelect?: (time_period: string, param: string) => void;
  renderComponent: string;
};

type DateRange = [Dayjs | null, Dayjs | null];

const timePeriodMapping = {
  'Today': timePeriodEnum.TODAY,
  'Yesterday': timePeriodEnum.YESTERDAY,
  'This Week': timePeriodEnum.THIS_WEEK,
  'Last Week': timePeriodEnum.LAST_WEEK,
  'Week to Date': timePeriodEnum.WEEK_TO_DATE,
  'This Month': timePeriodEnum.THIS_MONTH,
  'Last Month': timePeriodEnum.LAST_MONTH,
  'Month to Date': timePeriodEnum.MONTH_TO_DATE,
  'This Quarter': timePeriodEnum.THIS_QUARTER,
  'Last Quarter': timePeriodEnum.LAST_QUARTER,
  'Quarter to Date': timePeriodEnum.QUARTER_TO_DATE,
  'This Year': timePeriodEnum.THIS_YEAR,
  'Last Year': timePeriodEnum.LAST_YEAR,
  'Year to Date': timePeriodEnum.YEAR_TO_DATE,
  'All Time': timePeriodEnum.ALL_TIME,
  'Last 6 Months': timePeriodEnum.SIX_MONTHS,
};

const popularTimeperiods = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month'
];

const currentTimeperiods = [
  'Week to Date',
  'Month to Date',
  'This Quarter',
  'Quarter to Date',
  'This Year',
  'Year to Date'
];

const previousTimeperiods = [
  'Last Month',
  'Last Quarter',
  'Last 6 Months',
  'Last Year',
  'All Time'
];

const lastOptions = [
  { label: 'Last 7 Days', value: '7_DAYS', days: 7 },
  { label: 'Last 30 Days', value: '30_DAYS', days: 30 },
  { label: 'Last 90 Days', value: '90_DAYS', days: 90 },
  { label: 'Last 180 Days', value: '180_DAYS', days: 180 },
  { label: 'Last 365 Days', value: '365_DAYS', days: 365 }
];

const STORAGE_KEY = 'recent_timeperiods';
const MAX_RECENT_ITEMS = 5;

type RecentTimeperiod = {
  label: string;
  code: string;
  timestamp: number;
  startDate?: string;
  endDate?: string;
};

const getRecentTimeperiods = (): RecentTimeperiod[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return [];
  }
};

const addToRecentTimeperiods = (label: string, code: string, startDate?: string, endDate?: string) => {
  try {
    const recent = getRecentTimeperiods();
    // Remove if already exists
    const filtered = recent.filter(item => item.code !== code);
    // Add new item at the beginning
    const newRecent = [
      { label, code, timestamp: Date.now(), startDate, endDate },
      ...filtered
    ].slice(0, MAX_RECENT_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newRecent));
    return newRecent;
  } catch (error) {
    console.error('Error writing to localStorage:', error);
    return [];
  }
};

const formatDateRange = (start: string, end: string) => {
  const startDate = dayjs(start).format('MMM D, YYYY');
  const endDate = dayjs(end).format('MMM D, YYYY');
  return `${startDate} - ${endDate}`;
};

const getLastOptionLabel = (value: string) => {
  const option = lastOptions.find(opt => opt.value === value);
  return option ? option.label : '';
};

export const TimeperiodSelect = ({
  timeperiod,
  selectedTimeperiod,
  handleClickChangeTimeperiod,
  onLastOptionSelect,
  renderComponent,
}: TimeperiodSelectProps) => {
  const [dateRange, setDateRange] = useState<DateRange>([null, null]);
  const [sinceDate, setSinceDate] = useState<Dayjs | null>(null);
  const [expanded, setExpanded] = useState<string | false>('popular');
  const [lastValue, setLastValue] = useState<string>('');
  const [customRangeTab, setCustomRangeTab] = useState(0);
  const [recentTimeperiods, setRecentTimeperiods] = useState<RecentTimeperiod[]>(getRecentTimeperiods());
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<DateRange>([null, null]);
  const [tempSinceDate, setTempSinceDate] = useState<Dayjs | null>(null);

  // Reset accordion state when select is opened
  const handleSelectOpen = () => {
    setIsSelectOpen(true);
    setExpanded('popular');
    // Set temporary values to current values when opening
    setTempDateRange(dateRange);
    setTempSinceDate(sinceDate);
  };

  const handleSelectClose = () => {
    setIsSelectOpen(false);
    // Reset temporary values when closing without applying
    setTempDateRange(dateRange);
    setTempSinceDate(sinceDate);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    event.stopPropagation();
    setExpanded(isExpanded ? panel : false);
  };

  const handleTimeperiodSelection = (code: string, customLabel?: string, startDate?: string, endDate?: string) => {
    let label = customLabel;
    
    if (!customLabel) {
      const selectedItem = timeperiod.find(t => t.code === code);
      if (selectedItem) {
        label = selectedItem.name;
      }
    }

    if (label) {
      const newRecent = addToRecentTimeperiods(label, code, startDate, endDate);
      setRecentTimeperiods(newRecent);
    }
    
    handleClickChangeTimeperiod(code, label, startDate, endDate);
    setIsSelectOpen(false);
  };

  const handleTempDateRangeChange = (newValue: DateRange) => {
    setTempDateRange(newValue);
  };

  const handleTempSinceDateChange = (date: Dayjs | null) => {
    setTempSinceDate(date);
  };

  const handleApplyDateRange = () => {
    if (customRangeTab === 0) {
      if (tempDateRange[0] && tempDateRange[1]) {
        setDateRange(tempDateRange);
        const startDate = tempDateRange[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        const endDate = tempDateRange[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        const label = formatDateRange(tempDateRange[0].format('YYYY-MM-DD'), tempDateRange[1].format('YYYY-MM-DD'));
        handleTimeperiodSelection('CUSTOM_RANGE', `Custom: ${label}`, startDate, endDate);
      }
    } else {
      if (tempSinceDate) {
        setSinceDate(tempSinceDate);
        const today = dayjs().endOf('day');
        const startDate = tempSinceDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        const endDate = today.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        const label = formatDateRange(tempSinceDate.format('YYYY-MM-DD'), today.format('YYYY-MM-DD'));
        handleTimeperiodSelection('CUSTOM_RANGE', `Since ${tempSinceDate.format('MMM D, YYYY')}`, startDate, endDate);
      }
    }
    setIsSelectOpen(false);
  };

  const handleResetDateRange = () => {
    if (customRangeTab === 0) {
      setTempDateRange([null, null]);
    } else {
      setTempSinceDate(null);
    }
  };

  const handleLastChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setLastValue(value);
    const days = value.split('_')[0]; // Extract the number from "7_DAYS", "30_DAYS" etc.
    const label = getLastOptionLabel(value);
    handleTimeperiodSelection(`LAST&param=${days}`, label);
  };

  const handleCustomRangeTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCustomRangeTab(newValue);
  };

  if (renderComponent === renderComponentEnum.nurturingHistory) {
    return null;
  }

  const renderTimeperiodList = (items: string[]) => (
    <List disablePadding>
      {items.map((item) => {
        const timeperiodCode = timePeriodMapping[item as keyof typeof timePeriodMapping];
        const timeperiodItem = timeperiod.find((t) => t.code === timeperiodCode);
        if (!timeperiodItem) return null;
        
        const isSelected = selectedTimeperiod === timeperiodItem.code;
        
        return (
          <ListItem key={timeperiodItem.code} disablePadding>
            <ListItemButton
              onClick={() => handleTimeperiodSelection(timeperiodItem.code)}
              selected={isSelected}
              sx={{
                pl: 4,
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:hover': {
                  backgroundColor: 'primary.lighter',
                },
              }}
            >
              <ListItemText 
                primary={item}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isSelected ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  const renderRecentTimeperiods = () => (
    <List disablePadding>
      {recentTimeperiods.map((item) => {
        const isSelected = selectedTimeperiod === item.code;
        
        return (
          <ListItem key={item.code} disablePadding>
            <ListItemButton
              onClick={() => handleTimeperiodSelection(item.code, item.label, item.startDate, item.endDate)}
              selected={isSelected}
              sx={{
                pl: 4,
                py: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
                '&:hover': {
                  backgroundColor: 'primary.lighter',
                },
              }}
            >
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isSelected ? 500 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
      {recentTimeperiods.length === 0 && (
        <ListItem sx={{ pl: 4, py: 2 }}>
          <ListItemText 
            primary="No recent selections"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          />
        </ListItem>
      )}
    </List>
  );

  const getSelectedLabel = () => {
    // For regular time periods
    const selectedItem = timeperiod.find(t => t.code === selectedTimeperiod);
    if (selectedItem) {
      return selectedItem.name;
    }
    
    // For custom ranges and since dates, the label is stored in recent timeperiods
    const recentItem = recentTimeperiods.find(item => item.code === selectedTimeperiod);
    if (recentItem) {
      return recentItem.label;
    }
    
    return 'Select Timeperiod';
  };

  return (
    <FormControl
      sx={{
        width: 320,
        minWidth: 320,
        maxWidth: 320,
      }}
    >
      <InputLabel id="timeperiod_label" sx={{ fontSize: '0.95rem' }}>Select Timeperiod</InputLabel>
      <Select
        labelId="timeperiod_label"
        id="timeperiod_select"
        value={selectedTimeperiod}
        label="Select Timeperiod"
        open={isSelectOpen}
        onOpen={handleSelectOpen}
        onClose={handleSelectClose}
        onChange={(event) => handleTimeperiodSelection(event.target.value)}
        sx={{
          '& .MuiSelect-select': {
            fontSize: '0.95rem',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              width: 320,
              maxHeight: 500,
              '& .MuiAccordion-root': {
                '&:before': { display: 'none' },
                boxShadow: 'none',
                borderRadius: 0,
              },
            },
          },
        }}
        renderValue={() => getSelectedLabel()}
      >
        <MenuItem value="" sx={{ display: 'none' }}>
          <em>Select Timeperiod</em>
        </MenuItem>

        <Accordion
          expanded={expanded === 'popular'}
          onChange={handleAccordionChange('popular')}
          disableGutters
          sx={{ width: '100%' }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 48,
              backgroundColor: 'primary.lighter',
              '&:hover': { 
                backgroundColor: 'primary.lighter',
              },
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              }
            }}
          >
            <Typography 
              fontWeight={500} 
              fontSize="0.95rem"
              color="primary.dark"
            >
              Recently Used
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {renderRecentTimeperiods()}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'current'}
          onChange={handleAccordionChange('current')}
          disableGutters
          sx={{ width: '100%' }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 48,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              }
            }}
          >
            <Typography fontWeight={500} fontSize="0.95rem">Current Periods</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {renderTimeperiodList(currentTimeperiods)}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'previous'}
          onChange={handleAccordionChange('previous')}
          disableGutters
          sx={{ width: '100%' }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 48,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              }
            }}
          >
            <Typography fontWeight={500} fontSize="0.95rem">Previous Periods</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {renderTimeperiodList(previousTimeperiods)}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1.5 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={lastValue}
                  onChange={handleLastChange}
                  displayEmpty
                  sx={{ 
                    fontSize: '0.9rem',
                    '& .MuiSelect-select': { py: 1.5 }
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select custom duration</em>
                  </MenuItem>
                  {lastOptions.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      sx={{
                        fontSize: '0.9rem',
                        py: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'custom'}
          onChange={handleAccordionChange('custom')}
          disableGutters
          sx={{ width: '100%' }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 48,
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              }
            }}
          >
            <Typography fontWeight={500} fontSize="0.95rem">Custom Range</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={customRangeTab} 
                onChange={handleCustomRangeTabChange}
                variant="fullWidth"
                sx={{ 
                  minHeight: 40,
                  '& .MuiTab-root': {
                    minHeight: 40,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                  }
                }}
              >
                <Tab label="Date Range" />
                <Tab label="Since Date" />
              </Tabs>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {customRangeTab === 0 ? (
                <DateRangePicker
                  value={tempDateRange}
                  onChange={handleTempDateRangeChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { 
                      size: 'small',
                      sx: { 
                        '& .MuiInputBase-input': { 
                          fontSize: '0.9rem',
                          py: 1
                        }
                      }
                    },
                  }}
                />
              ) : (
                <DatePicker
                  label="Select Start Date"
                  value={tempSinceDate}
                  onChange={handleTempSinceDateChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { 
                      size: 'small',
                      fullWidth: true,
                      sx: { 
                        '& .MuiInputBase-input': { 
                          fontSize: '0.9rem',
                          py: 1
                        }
                      }
                    }
                  }}
                />
              )}
            </LocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetDateRange}
                sx={{ 
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  minWidth: '80px'
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyDateRange}
                disabled={customRangeTab === 0 ? !tempDateRange[0] || !tempDateRange[1] : !tempSinceDate}
                sx={{ 
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  minWidth: '80px'
                }}
              >
                Apply
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Select>
    </FormControl>
  );
}; 